import { Routes } from '@angular/router';
import { seoSegments, type CountryCode } from '@handyhand/i18n';

import { buildMainCatRoutes, buildSeoRoutes } from '@app/routes/seo.routes';
import { buildJobRoutes } from '@app/routes/jobs.routes';
import { buildRecyclingRoutes } from '@app/routes/recycling.routes';
import { buildStaticPaths } from '@app/routes/static-paths';

/**
 * Returns the country's own routes WITHOUT the shared wildcard/404 fallbacks.
 * Use `buildAppRoutes` for the single-country variant; use `buildUnionAppRoutes`
 * (in app.config.ts) when you want a single build to serve every supported
 * country.
 */
export function buildCountryAppRoutes(country: CountryCode): Routes {
  const staticPaths = buildStaticPaths(country);
  const seg = seoSegments(country);
  const isDk = country === 'dk';

  return [
    {
      path: staticPaths.landing,
      loadComponent: () =>
        import('@app/pages/landing-page/landing-page.component').then(
          (c) => c.LandingPageComponent,
        ),
    },
    {
      path: 'checklists',
      children: [
        {
          path: ':slug',
          loadComponent: () =>
            import('@app/pages/check-list-page/check-list-page.component').then(
              (c) => c.ChecklistPageComponent,
            ),
        },
      ],
    },
    {
      path: staticPaths.app,
      loadComponent: () =>
        import('@app/pages/app/app-landing/app-landing.component').then(
          (c) => c.AppLandingComponent,
        ),
    },
    {
      path: staticPaths.contentAuthorSaxo,
      loadComponent: () =>
        import('@app/pages/contentAuthorPage/contentAuthorPage.component').then(
          (c) => c.ContentAuthorComponent,
        ),
    },
    {
      path: staticPaths.updates,
      loadComponent: () =>
        import('@app/pages/changelog/changelog.component').then(
          (c) => c.ChangelogComponent,
        ),
    },
    {
      path: staticPaths.calculateIncome,
      loadComponent: () =>
        import('@app/pages/earningsCalculator/earningsCalculator.component').then(
          (c) => c.EarningsCalculatorComponent,
        ),
    },
    {
      path: staticPaths.handyhanderLevels,
      loadComponent: () =>
        import('@app/components/handyhandLevels/handyhanderLevels.component').then(
          (c) => c.HandyhanderLevelsComponent,
        ),
    },
    {
      path: staticPaths.categories,
      loadComponent: () =>
        import('@app/pages/seo/seo-categories/seo-categories.component').then(
          (m) => m.SeoCategoriesComponent,
        ),
    },
    {
      path: staticPaths.damageCompensation,
      loadComponent: () =>
        import('@app/pages/insurance/insurance.component').then(
          (c) => c.InsuranceComponent,
        ),
    },
    {
      path: staticPaths.privacyPolicy,
      children: [
        {
          path: staticPaths.dpiaChild,
          loadComponent: () =>
            import('@app/pages/privacy-policy/dpia/dpia.component').then(
              (c) => c.DpiaComponent,
            ),
        },
        {
          path: '',
          loadComponent: () =>
            import('@app/pages/privacy-policy/privacy-policy.component').then(
              (c) => c.PrivacyPolicyComponent,
            ),
        },
      ],
    },
    {
      path: staticPaths.termsAndAgreements,
      children: [
        {
          path: staticPaths.deleteAccountChild,
          loadComponent: () =>
            import('@app/pages/terms-and-conditions/delete-account/delete-account.component').then(
              (c) => c.DeletionComponent,
            ),
        },
        {
          path: '',
          loadComponent: () =>
            import('@app/pages/terms-and-conditions/terms-and-conditions.component').then(
              (c) => c.TermsAndConditionsComponent,
            ),
        },
      ],
    },
    {
      path: staticPaths.securityIncident,
      loadComponent: () =>
        import('@app/pages/security-incident/security-incident.component').then(
          (c) => c.SecurityIncidentComponent,
        ),
    },
    {
      path: staticPaths.aboutUs,
      loadComponent: () =>
        import('@app/pages/aboutUs/aboutUs.component').then(
          (c) => c.AboutUsComponent,
        ),
    },
    {
      path: staticPaths.findWork,
      loadComponent: () =>
        import('@app/pages/handyhanderLanding/handyhanderLanding').then(
          (c) => c.HandyhanderLanding,
        ),
    },
    {
      path: staticPaths.priceGuides,
      loadComponent: () =>
        import('@app/pages/guides/priceGuides/priceGuidesOverview/priceGuidesOverview').then(
          (c) => c.PriceGuidesOverviewComponent,
        ),
    },
    {
      path: seg.suffix.aiPriceGuide,
      loadComponent: () =>
        import('@app/pages/guides/aiPriceGuidesOverview/aiPriceGuidesOverview').then(
          (c) => c.AiPriceGuidesOverviewComponent,
        ),
    },
    {
      path: staticPaths.tipsIndex,
      loadComponent: () =>
        import('@app/pages/tips-index/tips-index.component').then(
          (c) => c.TipsIndexComponent,
        ),
    },
    {
      path: staticPaths.howItWorks,
      loadComponent: () =>
        import('@app/pages/howItWorksPage/howItWorksPage.component').then(
          (c) => c.HowItWorksPageComponent,
        ),
    },
    {
      path: staticPaths.search,
      loadComponent: () =>
        import('@app/pages/search-page/search-page.component').then(
          (c) => c.SearchPageComponent,
        ),
    },
    ...(isDk
      ? ([
          {
            path: staticPaths.blogPost,
            loadComponent: () =>
              import('@app/pages/blog/details/blog.detail.component').then(
                (c) => c.BlogDetailComponent,
              ),
          },
          {
            path: staticPaths.blog,
            loadComponent: () =>
              import('@app/pages/blog/blog.component').then(
                (c) => c.BlogComponent,
              ),
          },
          {
            path: staticPaths.press,
            loadComponent: () =>
              import('@app/pages/pressRoom/pressRoom.component').then(
                (c) => c.PressRoomComponent,
              ),
          },
        ] as Routes)
      : []),
    ...buildJobRoutes(country),
    ...buildRecyclingRoutes(country),
  ];
}

/**
 * The `/:headCategory/...` wildcard route block. Kept separate from the
 * country's static routes so the union builder can put every country's
 * static paths ahead of all `:headCategory` wildcards — otherwise the first
 * registered wildcard would greedily shadow the other country's static
 * pages (e.g. `/wie-zijn-wij` on NL, `/vindwerk` on NL).
 */
export function buildHeadCategoryRoute(country: CountryCode): Routes {
  return [
    {
      path: ':headCategory',
      children: [...buildMainCatRoutes(country), ...buildSeoRoutes(country)],
    },
  ];
}

export function buildFallbackRoutes(): Routes {
  return [
    {
      path: '404',
      loadComponent: () =>
        import('@components/shared/error/error.component').then(
          (c) => c.ErrorComponent,
        ),
    },
    {
      path: '**',
      loadComponent: () =>
        import('@components/shared/error/error.component').then(
          (c) => c.ErrorComponent,
        ),
    },
  ];
}

/**
 * Full Angular route set for a single country, including the shared 404/**
 * fallbacks. Kept for callers that want a single-country route tree.
 */
export function buildAppRoutes(country: CountryCode): Routes {
  return [
    ...buildCountryAppRoutes(country),
    ...buildHeadCategoryRoute(country),
    ...buildFallbackRoutes(),
  ];
}
